/**
 * Who is signed in to the Healtrics console (ADR-024).
 *
 * Its own keys, deliberately. The clinic portal and this console are served
 * from one origin for now, and a shared key would mean signing into one
 * silently ends the other: the same browser tab cannot hold two sessions under
 * `healtrics.refresh`. Separate keys let somebody run the console in one tab
 * and a clinic's portal in another, which is exactly what happens during a
 * demonstration.
 *
 * Shared origin is not the same as shared authority. The two sessions carry
 * different token audiences, so a console token presented to a portal endpoint
 * is refused before any permission logic runs, and the reverse too. What the
 * shared origin does mean is that a script on this origin can read both, which
 * is the same exposure RAID I-09 already records for the portal: before real
 * patients, both refresh tokens belong in httpOnly cookies.
 *
 * There is no active clinic here, and there is no way to set one. A platform
 * administrator holds none, and row-level security reads an unset clinic as
 * "no rows", so the clinical tables are closed to this session at the database
 * (ADR-021, ADR-024 section 3).
 */

const REFRESH_KEY = "healtrics.platform.refresh";
const WHO_KEY = "healtrics.platform.who";

let accessToken: string | null = null;

const listeners = new Set<() => void>();

function announce(): void {
  listeners.forEach((listener) => listener());
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getRefreshToken(): string | null {
  try {
    return sessionStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

export function isSignedIn(): boolean {
  return accessToken !== null;
}

/** The signed-in person's name. Not a credential. */
export function getWho(): string | null {
  try {
    return sessionStorage.getItem(WHO_KEY);
  } catch {
    return null;
  }
}

export function startSession(session: {
  access_token: string;
  refresh_token: string;
  full_name?: string;
}): void {
  accessToken = session.access_token;
  try {
    sessionStorage.setItem(REFRESH_KEY, session.refresh_token);
    if (session.full_name) sessionStorage.setItem(WHO_KEY, session.full_name);
  } catch {
    // A browser that refuses storage still gets a working session for this
    // page view; it ends at the next reload instead of surviving it.
  }
  announce();
}

export function replaceTokens(session: {
  access_token: string;
  refresh_token: string;
}): void {
  accessToken = session.access_token;
  try {
    sessionStorage.setItem(REFRESH_KEY, session.refresh_token);
  } catch {
    /* as above */
  }
}

export function endSession(): void {
  accessToken = null;
  try {
    sessionStorage.removeItem(REFRESH_KEY);
    sessionStorage.removeItem(WHO_KEY);
  } catch {
    /* as above */
  }
  announce();
}
